/**
 * Discover page - sponsor profile discovery for friends.
 * 
 * Elm-ish architecture: This page is THIN.
 * - Uses useSponsorDiscoveryProgram for state management
 * - Delegates rendering to SponsorDiscoveryView
 * - No duplicated rendering logic
 * 
 * Adapts content based on registrationKind:
 * - Friends: Browse sponsors looking for connections
 * - Sponsors: Browse friends looking for introductions
 * 
 * Security posture:
 * - Identity-minimized display (display_name, headline, bio, city_or_region)
 * - No account_id, email, phone, or verification fields
 * - Demo-safe loading and empty states
 */
import { useEffect } from 'react';
import { useSponsorDiscoveryProgram } from '@/features/sponsor-discovery/useSponsorDiscoveryProgram';
import { SponsorDiscoveryView } from '@/features/sponsor-discovery/view';
import { useAuthStore } from '@/store/authStore';

/**
 * Discover page - uses SponsorDiscoveryView for rendering.
 * 
 * The page is responsible for:
 * - Starting the discovery program on mount
 * - Passing callbacks to the view
 * - Adapting copy based on registrationKind
 * 
 * All rendering logic is delegated to SponsorDiscoveryView.
 */
export function DiscoverPage() {
  const program = useSponsorDiscoveryProgram();
  const registrationKind = useAuthStore(state => state.registrationKind);
  const isFriend = registrationKind === 'friend';

  // Start loading on mount
  useEffect(() => {
    program.start();
  }, [program.start]);

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">Discover</h1>
        <p className="page-description">
          {isFriend
            ? 'Browse sponsors looking for meaningful connections.'
            : 'Discover friends looking for connections.'}
        </p>
      </div>

      <div className="discover-content">
        <SponsorDiscoveryView
          model={program.model}
          onSkip={program.skip}
          onSave={program.save}
          onRequestIntro={program.requestIntro}
          onRetry={program.retry}
          onLoadMore={program.loadMore}
          isSponsor={registrationKind === 'sponsor'}
          isFriend={isFriend}
        />
      </div>

      <style>{`
        .discover-content {
          margin-top: var(--spacing);
        }
        
        /* Discovery container - centers the active card */
        .discovery-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: var(--spacing);
          padding: var(--spacing) 0;
        }
        
        /* Active card container */
        .active-card-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          width: 100%;
          max-width: 400px;
          gap: var(--spacing);
        }
        
        /* Active card styling - light romantic card with dark text for contrast */
        .profile-card.active-card {
          width: 100%;
          background: linear-gradient(180deg, #fff7fb 0%, #f3efff 100%);
          color: #171423;
          border: 1px solid rgba(255, 255, 255, 0.18);
          border-radius: 20px;
          box-shadow: 0 24px 80px rgba(0, 0, 0, 0.35);
          padding: var(--spacing);
        }
        
        /* Card header with title and badge */
        .profile-card.active-card .card-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 12px;
        }
        
        .profile-card.active-card .card-title {
          color: #171423;
          font-size: 1.25rem;
          font-weight: 600;
          margin: 0;
        }
        
        .profile-card.active-card .card-badge {
          background: rgba(233, 69, 96, 0.12);
          color: #c41e4a;
          font-size: 11px;
          font-weight: 600;
          padding: 4px 10px;
          border-radius: 12px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        
        .profile-card.active-card .card-headline {
          color: #3d3347;
          font-size: 1rem;
          font-weight: 500;
          margin-bottom: 12px;
        }
        
        .profile-card.active-card .card-bio {
          color: #4a4458;
          font-size: 0.9rem;
          line-height: 1.5;
          margin-bottom: 12px;
        }
        
        .profile-card.active-card .card-location {
          display: flex;
          align-items: center;
          gap: 6px;
          color: #6b5f7a;
          font-size: 0.85rem;
          margin-bottom: 12px;
        }
        
        .profile-card.active-card .card-location .location-icon {
          font-size: 14px;
        }
        
        .profile-card.active-card .card-footer {
          border-top: 1px solid rgba(0, 0, 0, 0.06);
          padding-top: 12px;
          margin-top: 12px;
        }
        
        .profile-card.active-card .card-updated {
          color: #8a7a9a;
          font-size: 0.8rem;
        }
        
        /* Card actions container - centered button row */
        .card-actions {
          display: flex;
          justify-content: center;
          gap: 20px;
          width: 100%;
          padding: var(--spacing) 0;
        }
        
        /* Individual action button - consistent sizing and tactile feel */
        .action-btn {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 6px;
          border: none;
          border-radius: 50%;
          width: 72px;
          height: 72px;
          cursor: pointer;
          transition: transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.2s ease;
          font-size: 12px;
          font-weight: 600;
        }
        
        .action-btn:focus-visible {
          outline: 3px solid var(--color-accent);
          outline-offset: 3px;
        }
        
        .action-btn:hover:not(:disabled) {
          transform: scale(1.08);
        }
        
        .action-btn:active:not(:disabled) {
          transform: scale(0.95);
        }
        
        .action-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        
        .action-icon {
          font-size: 26px;
          line-height: 1;
        }
        
        .action-label {
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.3px;
        }
        
        /* Skip button - neutral, subtle */
        .action-btn.skip-btn {
          background: rgba(255, 255, 255, 0.08);
          color: #9a8aaa;
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
        }
        
        .action-btn.skip-btn:hover:not(:disabled) {
          background: rgba(255, 255, 255, 0.12);
          box-shadow: 0 6px 20px rgba(0, 0, 0, 0.25);
        }
        
        /* Save button - PRIMARY action, prominent styling */
        .action-btn.save-btn {
          background: linear-gradient(135deg, #e94560 0%, #c41e4a 100%);
          color: #fff;
          box-shadow: 0 8px 32px rgba(233, 69, 96, 0.4);
          width: 80px;
          height: 80px;
        }
        
        .action-btn.save-btn:hover:not(:disabled) {
          background: linear-gradient(135deg, #ff5a75 0%, #e94560 100%);
          box-shadow: 0 12px 40px rgba(233, 69, 96, 0.5);
          transform: scale(1.12);
        }
        
        .action-btn.save-btn .action-icon {
          font-size: 30px;
        }
        
        .action-btn.save-btn .action-label {
          font-size: 12px;
        }
        
        /* Intro button - secondary action */
        .action-btn.intro-btn {
          background: rgba(255, 255, 255, 0.08);
          color: #b8a8d0;
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
        }
        
        .action-btn.intro-btn:hover:not(:disabled) {
          background: rgba(255, 255, 255, 0.12);
          box-shadow: 0 6px 20px rgba(0, 0, 0, 0.25);
        }
        
        /* FIX: Intro button disabled state for friends */
        .intro-btn-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 4px;
        }
        
        .action-btn.intro-btn-disabled {
          opacity: 0.5;
          cursor: not-allowed;
          background-color: #9ca3af;
        }
        
        .action-btn.intro-btn-disabled:hover {
          opacity: 0.5;
        }
        
        .intro-disabled-reason {
          font-size: 0.7rem;
          color: #6b7280;
          text-align: center;
          max-width: 80px;
        }
        
        /* Action error display */
        .action-error {
          padding: var(--spacing-sm);
          background: rgba(233, 69, 96, 0.15);
          color: #ff8a9a;
          border-radius: 12px;
          font-size: 14px;
          text-align: center;
          width: 100%;
          max-width: 300px;
          border: 1px solid rgba(233, 69, 96, 0.3);
        }
        
        /* Cards remaining counter - subdued */
        .cards-remaining {
          font-size: 13px;
          color: var(--color-text-muted);
          text-align: center;
          opacity: 0.8;
        }
        
        /* Load more button */
        .load-more-btn {
          margin-top: var(--spacing);
          padding: var(--spacing-sm) var(--spacing);
          background: var(--color-primary);
          color: var(--color-text);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 12px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          transition: background 0.2s, border-color 0.2s;
        }
        
        .load-more-btn:hover:not(:disabled) {
          background: #1a4a7a;
          border-color: rgba(255, 255, 255, 0.2);
        }
        
        .load-more-btn:focus-visible {
          outline: 2px solid var(--color-accent);
          outline-offset: 2px;
        }
        
        .load-more-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        
        /* Privacy notice - quiet pill-style banner */
        .privacy-notice {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 10px 16px;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 20px;
          margin-bottom: var(--spacing);
        }
        
        .privacy-notice .privacy-icon {
          font-size: 14px;
          opacity: 0.7;
        }
        
        .privacy-notice .notice-text {
          font-size: 12px;
          color: var(--color-text-muted);
          margin: 0;
          line-height: 1.4;
        }
        
        /* Empty state - friendly and subdued */
        .profile-list-empty {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: var(--spacing) var(--spacing);
          text-align: center;
          min-height: 200px;
        }
        
        .profile-list-empty .empty-title {
          font-size: 1.1rem;
          font-weight: 500;
          color: var(--color-text);
          margin: 0 0 8px 0;
          opacity: 0.9;
        }
        
        .profile-list-empty .empty-hint {
          font-size: 0.9rem;
          color: var(--color-text-muted);
          margin: 0;
          opacity: 0.7;
        }
        
        /* Loading state */
        .profile-list-loading {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: var(--spacing);
          color: var(--color-text-muted);
        }
        
        /* Error state */
        .profile-list-error {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: var(--spacing);
          text-align: center;
        }
        
        .profile-list-error .error-text {
          color: var(--color-accent);
          margin: 0 0 8px 0;
        }
        
        .profile-list-error .error-hint {
          color: var(--color-text-muted);
          font-size: 0.9rem;
          margin: 0 0 16px 0;
        }
        
        .profile-list-error .retry-button {
          padding: 10px 20px;
          background: var(--color-primary);
          color: var(--color-text);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 8px;
          cursor: pointer;
          font-size: 14px;
          transition: background 0.2s;
        }
        
        .profile-list-error .retry-button:hover {
          background: #1a4a7a;
        }
        
        .profile-list-error .retry-button:focus-visible {
          outline: 2px solid var(--color-accent);
          outline-offset: 2px;
        }
        
        /* Reduced motion: disable spring animations for users who prefer less motion */
        @media (prefers-reduced-motion: reduce) {
          .action-btn,
          .load-more-btn,
          .retry-button {
            transition: none;
          }
          
          .action-btn:hover:not(:disabled),
          .action-btn.save-btn:hover:not(:disabled) {
            transform: none;
          }
        }
      `}</style>
    </div>
  );
}
